import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { ArrowLeft, Loader2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useCondemnation } from "@/features/condemnation/api";
import { CondemnationStatusBadge } from "@/features/condemnation/badges";
import { CommitteeDecisionPanel } from "@/features/condemnation/CommitteeDecisionPanel";
import { CondemnationCertificateButton } from "@/features/condemnation/Certificate";
import { WorkOrderHistoryEvidence } from "@/features/condemnation/WorkOrderHistoryEvidence";

export const Route = createFileRoute("/_authenticated/condemnation/$id")({
  component: CondemnationDetail,
});

function CondemnationDetail() {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const { data: row, isLoading } = useCondemnation(id);

  if (isLoading) return <div className="grid place-items-center py-20"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;
  if (!row) return <div className="py-20 text-center text-muted-foreground">You do not have access to this condemnation record.</div>;

  return (
    <div className="space-y-4">
      <Button variant="ghost" size="sm" onClick={() => navigate({ to: "/condemnation" })}>
        <ArrowLeft className="mr-1 h-4 w-4" />Back
      </Button>

      <Card>
        <CardContent className="flex flex-wrap items-start justify-between gap-3 p-5">
          <div className="space-y-1">
            <Badge variant="outline" className="font-mono">{row.request_no}</Badge>
            <div className="text-sm"><span className="font-mono text-xs">{row.asset?.asset_code}</span> · {row.asset?.name}</div>
            <CondemnationStatusBadge status={row.status} />
          </div>
          <CondemnationCertificateButton row={row} />
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Request</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-1 gap-3 md:grid-cols-2 text-sm">
          <Info label="Reason">{row.reason}</Info>
          <Info label="Repair Estimate">{row.repair_cost_estimate ?? "—"}</Info>
          <Info label="Replacement Estimate">{row.replacement_cost_estimate ?? "—"}</Info>
          <Info label="Detail" full>{row.reason_detail ?? "—"}</Info>
        </CardContent>
      </Card>

      {row.asset_id ? <WorkOrderHistoryEvidence assetId={row.asset_id} reason={row.reason} /> : null}

      <CommitteeDecisionPanel row={row} />
    </div>
  );
}

function Info({ label, children, full }: { label: string; children: React.ReactNode; full?: boolean }) {
  return (
    <div className={full ? "md:col-span-2" : ""}>
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="text-sm font-medium">{children}</div>
    </div>
  );
}
